import { IconButton, Stack, Typography } from "@mui/material";
import { ChevronLeftIcon } from "../../images";
import { useEffect, useState } from "react";
import { AnalyticCardWrapper } from "./AnalyticCardWrapper";
import { GetMoreContactsForAccountBtn } from "../../ui/GetMoreContactsForAccountBtn";
import { useParams } from "react-router-dom";
import { GetMoreContactsForAccountsBtn } from "../../ui/GetMoreContactsForAccountsBtn";
import { useAppSelector } from "../../../hooks";

const SKIP = 3;

const Pagination = ({ data, page }: { data: string[]; page: number }) => {
  const start = page * SKIP;
  const end = start + SKIP;

  return (
    <Stack gap="10px">
      {data.slice(start, end).map((el, ind) => (
        <Typography
          key={el + ind}
          variant="subtitle1"
          color="#363636"
          sx={{ fontWeight: 500, lineHeight: "21px", fontSize: "15px" }}
        >
          {`· ${el}`}
        </Typography>
      ))}
    </Stack>
  );
};

const PaginationGaps = ({
  totalPages,
  setPage,
  page,
}: {
  totalPages: number;
  setPage: (page: number) => void;
  page: number;
}) => {
  const incrementPage = () => {
    setPage(page + 1);
  };

  const decrementPage = () => {
    setPage(page - 1);
  };

  return (
    <Stack gap="6px" alignItems="center" flexDirection="row">
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls="previous-page"
        onClick={decrementPage}
        disabled={page === 0}
      >
        <ChevronLeftIcon width="24" color="currentColor" />
      </IconButton>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls="next-page"
        onClick={incrementPage}
        disabled={page === totalPages - 1}
      >
        <ChevronLeftIcon width="24" rotate="180" color="currentColor" />
      </IconButton>
    </Stack>
  );
};

type GapsContentProps = {
  gaps: string[];
};

const GapsContent = ({ gaps }: GapsContentProps) => {
  const { isEditor } = useAppSelector((state) => state.company.userRoles);

  const { account_id, campaign_id } = useParams();
  const [page, setPage] = useState<number>(0);

  useEffect(() => {
    setPage(0);
  }, [gaps]);

  return (
    <Stack gap="12px" justifyContent="space-between" flexGrow={1}>
      {gaps.length === 0 ? (
        <Stack alignItems="center" justifyContent="center" flexGrow={1}>
          <Typography
            variant="subtitle1"
            color="#363636"
            textAlign="center"
            maxWidth="240px"
            fontWeight={500}
          >
            No data yet
          </Typography>
          <Typography
            variant="body1"
            color="#797F84"
            textAlign="center"
            maxWidth="240px"
          >
            Gaps will appear once we’ll have more contacts details
          </Typography>
        </Stack>
      ) : (
        <Stack gap="8px" width="100%">
          <Pagination page={page} data={gaps} />
        </Stack>
      )}

      <Stack direction="row" gap="8px" justifyContent="space-between">
        <Stack flexDirection="row" gap="8px" flexWrap="wrap">
          {account_id && isEditor && (
            <GetMoreContactsForAccountBtn account_id={account_id} />
          )}
          {campaign_id && !account_id && isEditor && (
            <GetMoreContactsForAccountsBtn campaign_id={campaign_id} />
          )}
        </Stack>
        {gaps.length > 0 && (
          <PaginationGaps
            totalPages={Math.ceil(gaps.length / SKIP)}
            setPage={setPage}
            page={page}
          />
        )}
      </Stack>
    </Stack>
  );
};

type Props = {
  titleGaps: string[];
  isRoleChartBtnDisabled?: boolean;
};

export const GapsBlock = ({ titleGaps, isRoleChartBtnDisabled }: Props) => {
  return (
    <AnalyticCardWrapper
      tooltip="Possible gaps in buying committee roles and audience in the available account data.
       Gaps are identified based on the target audience and typical buying committee roles profile."
      title="Gap Analysis"
      isRoleChartBtnDisabled={isRoleChartBtnDisabled}
    >
      <>
        <Typography
          variant="button"
          color="#1F293299"
          sx={{ lineHeight: "20px", fontWeight: 400 }}
        >
          Missing Job Titles
        </Typography>
        <GapsContent gaps={titleGaps || []} />
      </>
    </AnalyticCardWrapper>
  );
};
